import { useBranding } from "@/contexts/BrandContext";
import { readCachedBrand } from "@/contexts/brandCache";

interface Props {
  variant?: "dark" | "light";
  height?: number;
  className?: string;
  /**
   * Render only the "DDK Logistic" wordmark (no emblem) for the bundled
   * platform brand. Used in the narrow `h-14` sidebar headers where the full
   * emblem+wordmark lockup reads too small. Ignored when the active brand
   * supplies its own logo — a white-labeled dealer still gets its full asset.
   */
  wordmark?: boolean;
}

// Bundled platform logos, used whenever the active brand provides no logo.
// Vector lockup: `-dark` recolors the navy ink to white for the dark sidebar.
const FALLBACK_DARK = "/ddk-logistic-logo-dark.svg";
const FALLBACK_LIGHT = "/ddk-logistic-logo.svg";
// Wordmark-only crop of the same artwork (no emblem), same two variants.
const WORDMARK_DARK = "/ddk-logistic-wordmark-dark.svg";
const WORDMARK_LIGHT = "/ddk-logistic-wordmark.svg";

/**
 * Brand-aware logo. Reads `logo_dark_url` (for `variant="dark"`, e.g. on the
 * navy sidebar) or `logo_url` (light backgrounds) from the active brand and
 * falls back to the bundled DDK assets when the brand provides none — so the
 * default brand renders exactly as before.
 *
 * Before the async brand resolves, the initial render prefers the host-cached
 * logo URL (BUG-245) so the logo does not start as the bundled DDK asset and
 * then swap — the inline pre-paint script preloads these URLs, so the first
 * paint shows the dealer logo instead of a flash. The live brand takes over
 * once it resolves (and is the same URL, so no visible swap).
 */
export function AppLogo({
  variant = "light",
  height,
  className,
  wordmark = false,
}: Props) {
  const branding = useBranding();
  const brandSrc =
    variant === "dark" ? branding.logo_dark_url : branding.logo_url;
  const cached = branding.is_default ? readCachedBrand() : null;
  const cachedSrc =
    variant === "dark" ? cached?.logo_dark_url : cached?.logo_url;
  // Wordmark crop only applies to the bundled platform fallback; a branded
  // dealer keeps its own full logo.
  const fallback = wordmark
    ? variant === "dark"
      ? WORDMARK_DARK
      : WORDMARK_LIGHT
    : variant === "dark"
      ? FALLBACK_DARK
      : FALLBACK_LIGHT;
  const src = brandSrc || cachedSrc || fallback;
  const alt =
    branding.brand_name || cached?.brand_name || "DDKSoft Logistic Platform";

  return (
    <img
      src={src}
      alt={alt}
      style={height ? { height, width: "auto" } : undefined}
      className={`block${className ? ` ${className}` : ""}`}
    />
  );
}
